import { Location } from '@angular/common';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, InjectionToken, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, filter, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { UserType } from '../../../types';
import { AuthService } from '../../../services/auth/auth.service';
import { AdvisorSessionRecoveryService } from '../../../services/auth/advisor-session-recovery.service';
import { GlobalDataService } from '../../../services/global-data/global-data.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { AdministrationService } from '../../../services/administration/administration.service';
import { StoreService } from '../../../services/store/store.service';

export const ADVISOR_SESSION_SWITCH_CONFIRM = new InjectionToken<(message: string) => boolean>(
  'Advisor session switch confirmation', { factory: () => message => window.confirm(message) }
);

@Component({
  selector: 'app-store-login',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './store-login.component.html',
  styleUrl: './store-login.component.css',
})
export class StoreLoginComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  authenticating = true;
  errorMessage = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private globalData: GlobalDataService,
    private authService: AuthService,
    private storeService: StoreService,
    private snackbarService: SnackbarService,
    private location: Location,
    private recovery: AdvisorSessionRecoveryService,
    private cdr: ChangeDetectorRef,
    private administration: AdministrationService,
    @Inject(ADVISOR_SESSION_SWITCH_CONFIRM) private confirmSessionSwitch: (message: string) => boolean
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(
      map(params => params['token'] as string | undefined),
      switchMap(token => {
        // Scrub before starting work; replaceState does not emit queryParams.
        this.location.replaceState('/storelogin');
        if (!token?.trim()) {
          this.setError('Login kunne ikke gennemføres. Prøv igen.');
          return EMPTY;
        }
        const expected = this.recovery.expectedExchange();
        const explicitIntent = this.recovery.consumeLoginIntent();
        const activeSession = this.globalData.getSessionData().value;
        const boundRecovery = Number.isSafeInteger(expected.expectedUserId) && !!expected.expectedSessionVersion;
        if (!explicitIntent && !boundRecovery && !this.confirmSessionSwitch(activeSession.initialized
            ? 'Du er allerede logget ind. Vil du skifte til butikssessionen fra dette link?'
            : 'Vil du logge ind i SAM som butik fra dette link?')) {
          const destination = !activeSession.initialized ? '/' :
            activeSession.userType === UserType.AgillicAdvisor ? '/sendouts' : '/overview';
          void this.router.navigateByUrl(destination, { replaceUrl: true });
          this.authenticating = false;
          this.cdr.markForCheck();
          return EMPTY;
        }
        this.errorMessage = '';
        this.authenticating = true;
        this.cdr.markForCheck();
        return this.authService.authenticateToken(token, expected).pipe(
          catchError(() => {
            this.setError('Login kunne ikke gennemføres. Prøv igen.');
            return EMPTY;
          }),
          filter((response): response is NonNullable<typeof response> => {
            if (!response || response.userType !== UserType.AgillicAdvisor) {
              this.setError('Login kunne ikke gennemføres. Prøv igen.');
              return false;
            }
            return true;
          }),
          switchMap(response => this.administration.getMyRoles().pipe(
            switchMap(({ matchedStoreId }) => {
              if (!this.isCurrent(response)) return EMPTY;
              if (matchedStoreId === null) return of({ response, store: null });
              return this.storeService.getStoreById(matchedStoreId).pipe(
                map(store => {
                  if (!store) throw new Error('Matched store could not be loaded');
                  return { response, store };
                })
              );
            }),
            catchError(() => {
              this.setError('Adgangen kunne ikke indlæses. Prøv igen.');
              return EMPTY;
            })
          ))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(({ response, store }) => {
      if (!this.isCurrent(response)) return;
      if (store) this.globalData.selectEditorStore(store);
      const result = this.recovery.completeLogin(store?.id ?? null);
      this.authenticating = false;
      this.cdr.markForCheck();
      if (result.storeChanged) this.snackbarService.show('Dit kladdearbejde blev kasseret, fordi du er logget ind i en anden butik.');
      this.router.navigateByUrl(store ? result.returnUrl : '/landing', { replaceUrl: true });
    });
  }

  private isCurrent(response: { generation?: number; userId: number }): boolean {
    const session = this.globalData.getSessionData().value;
    return response.generation !== undefined && this.globalData.isAuthenticationCurrent(response.generation) &&
      session.initialized && session.userId === response.userId && !!session.sessionVersion;
  }

  retryLogin(): void {
    this.errorMessage = '';
    this.authenticating = true;
    this.cdr.markForCheck();
    if (!this.recovery.retryLogin()) {
      this.setError('Login kunne ikke startes. Prøv igen.');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setError(message: string): void {
    this.errorMessage = message;
    this.authenticating = false;
    this.cdr.markForCheck();
  }
}
